import classNames from "classnames/bind";
import { Product, RuntimeConfig, FeatureFlags } from "@/types/types";
import { ChangeEvent, useEffect, useState } from "react";
import { Accordion } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/Accordion";
import { AccordionItem } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/accordion-item/AccordionItem";
import {
  PrimaryButton,
  UnderlinedButton,
} from "@JohnLewisPartnership/jl-design-system/dist/components/button/Button";
import RadioButton from "@JohnLewisPartnership/jl-design-system/dist/components/radio-button/RadioButton";
import OptionList from "@JohnLewisPartnership/jl-design-system/dist/components/option/OptionList";
import { TextInput } from "@JohnLewisPartnership/jl-design-system/dist/components/text-input/TextInput";
import { DEPRECATED_Message } from "@JohnLewisPartnership/jl-design-system/dist/components/message/Message";
import { HorizontalRule } from "@/components/HorizontalRule/HorizontalRule";
import Price from "@JohnLewisPartnership/jl-design-system/dist/components/price/Price";
import { TickCircleFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/TickCircleFilled";
import { ErrorFilled } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ErrorFilled";

import styles from "./RomanBlindsForm.module.scss";
import {
  M2M_ROMAN_BLINDS_MIN_HEIGHT,
  M2M_ROMAN_BLINDS_MIN_INSTALLATION_HEIGHT,
  M2M_ROMAN_BLINDS_MIN_WIDTH,
  M2M_DEFAULT_LINING_WEIGHT,
  M2M_ROMAN_BLINDS_DEFAULT_STACKING_STYLE,
} from "../../util/constants";
import { useVariant } from "@/hooks/useVariant/useVariant";
import {
  getM2MPriceWithBlackout,
  M2MError,
  M2MErrorType,
} from "../../util/getM2MPrice";
import { MadeToMeasureReviewModal } from "../MadeToMeasureReviewModal/MadeToMeasureReviewModal";
import Link from "next/link";
import { PriceError } from "../PriceError/PriceError";
import { formatM2MTitle } from "@/utils/product/formatM2MTitle";
import {
  LiningWeight,
  RomanBlindConfiguration,
  StackingStyle,
} from "../../configurationTypes";
import {
  getConfigFromRomanBlindsCookie,
  setRomanBlindsCookieFromConfig,
} from "../../util/madeToMeasureCookieHelper";
import {
  M2MHelpSection,
  MadeToMeasureHelpModal,
} from "../MadeToMeasureHelpModal/MadeToMeasureHelpModal";

const cx = classNames.bind(styles);

type Props = {
  product: Product;
  runtimeConfig: RuntimeConfig;
  flags: FeatureFlags;
};

type MeasurementFieldProps = {
  value: string;
  valid: boolean;
  error: string;
};

type MeasurementFields = {
  width: MeasurementFieldProps;
  height: MeasurementFieldProps;
  installationHeight: MeasurementFieldProps;
};

type RadioButtonProps = {
  value: string;
};

type StackingStyleRadioButtonProps = {
  value: StackingStyle;
};

type LiningWeightRadioButtonProps = {
  value: LiningWeight;
};

type RadioButtons = {
  stackingStyle: StackingStyleRadioButtonProps;
  controlSide: RadioButtonProps;
  liningWeight: LiningWeightRadioButtonProps;
  liningColour: RadioButtonProps;
};

export const RomanBlindsForm = ({ product, runtimeConfig, flags }: Props) => {
  const { variant } = useVariant(product);

  const priceFrom = { now: variant.price?.value };

  const [measurementFields, setMeasurementsFields] =
    useState<MeasurementFields>({
      width: { value: "", valid: false, error: "" },
      height: { value: "", valid: false, error: "" },
      installationHeight: { value: "", valid: false, error: "" },
    });

  const [radioButtonValues, setRadioButtonValues] = useState<RadioButtons>({
    stackingStyle: { value: M2M_ROMAN_BLINDS_DEFAULT_STACKING_STYLE },
    controlSide: { value: "Right" },
    liningWeight: { value: M2M_DEFAULT_LINING_WEIGHT },
    liningColour: { value: "Cream" },
  });

  const [minInstallHeight, setMinInstallHeight] = useState(
    M2M_ROMAN_BLINDS_MIN_INSTALLATION_HEIGHT,
  );
  const [minInstallHeightHint, setInstallHeightHint] = useState("");
  const [priceError, setPriceError] = useState<M2MError>({
    heading: "",
    body: "",
    type: "",
    details: undefined,
  });
  const [totalPrice, setTotalPrice] = useState("");
  const [blackoutPrice, setBlackoutPrice] = useState("");
  const [isReviewButtonEnabled, setIsReviewButtonEnabled] = useState(false);
  const [isPriceCalculating, setIsPriceCalculating] = useState(false);
  const [maxRailSize, setMaxRailSize] = useState("");
  const [maxDrop, setMaxDrop] = useState("");

  const [formUpdatedFromCookie, setFormUpdatedFromCookie] = useState(false);

  const getPreviewImageUrl = (suffix = "") => {
    return suffix
      ? variant?.images.alternatives.find((img) => img.endsWith(suffix))
      : "";
  };

  const [selectedPreviewImage, setSelectedPreviewImage] = useState(
    getPreviewImageUrl("WSRB"),
  );

  const [helpModal, updateHelpModal] = useState({
    open: false,
    visibleSection: { name: "", title: "" },
  });

  const helpSections: M2MHelpSection[] = [
    { name: "roman-blinds-stacking-styles", title: "Stacking Styles" },
    { name: "roman-blinds-measurements", title: "Measurements" },
    { name: "roman-blinds-lining-options", title: "Lining Options" },
  ];

  const toggleHelpModal = (section: M2MHelpSection = { name: "", title: "" }) =>
    updateHelpModal({ open: !helpModal.open, visibleSection: section });

  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const getFabric = () => ({
    variantId: variant.id,
    sku: variant.aliases.skuId,
    displayCode: variant.displayCode,
    productName: variant.title,
  });

  const getConfigFromFormData = (): RomanBlindConfiguration => {
    return {
      type: "RomanBlind",
      stackingStyle: radioButtonValues.stackingStyle.value,
      width: parseFloat(measurementFields.width.value),
      height: parseFloat(measurementFields.height.value),
      installationHeight: parseFloat(
        measurementFields.installationHeight.value,
      ),
      liningWeight: radioButtonValues.liningWeight.value,
      liningColour: radioButtonValues.liningColour.value,
      controlSide: radioButtonValues.controlSide.value,
    };
  };

  const setFormDataFromConfig = (config: RomanBlindConfiguration) => {
    const {
      stackingStyle,
      controlSide,
      liningWeight,
      liningColour,
      width,
      height,
      installationHeight,
    } = config;
    setRadioButtonValues({
      stackingStyle: {
        value: stackingStyle,
      },
      controlSide: {
        value: controlSide,
      },
      liningWeight: {
        value: liningWeight,
      },
      liningColour: {
        value: liningColour,
      },
    });

    setMeasurementsFields({
      width: {
        value: width ? width.toString() : "",
        valid: width > 0,
        error: "",
      },
      height: {
        value: height ? height.toString() : "",
        valid: height > 0,
        error: "",
      },
      installationHeight: {
        value:
          installationHeight && installationHeight >= height
            ? installationHeight.toString()
            : "",
        valid: installationHeight >= height && installationHeight > 0,
        error: "",
      },
    });

    setFormUpdatedFromCookie(true);
  };

  useEffect(() => {
    if (
      formUpdatedFromCookie &&
      radioButtonValues.stackingStyle.value === StackingStyle.SelfStack
      // only need to request if matches default value
    ) {
      requestPrice();
      setFormUpdatedFromCookie(false);
    }
  }, [formUpdatedFromCookie, radioButtonValues.stackingStyle.value]);

  const isDecimalValueToMax2Places = (value: string) => {
    const regExDecimalCondition = /^[0-9]+(\.([0-9]{1,2})?)?$/;
    return regExDecimalCondition.test(value);
  };

  const checkAllMeasurementsValid = () => {
    const measurementsValid = Object.values(measurementFields).every(
      (field) => field.valid && !field.error,
    );
    const radioButtonsValid = Object.values(radioButtonValues).every(
      (field) => field.value !== "",
    );
    return measurementsValid && radioButtonsValid;
  };

  const getErrorMessage = (field: string, type: string, value: string) => {
    if (field === "installationHeight") field = "installation height";
    return `This is ${type === "max" ? "above" : "below"} the ${type}imum ${field} of ${value} centimetres ${field !== "installation height" ? "available for this fabric" : ""}`.trimEnd();
  };

  const formatDecimalValue = (value: string, name: string) => {
    if (!isDecimalValueToMax2Places(value)) {
      setMeasurementsFields({
        ...measurementFields,
        [name]: { value: parseFloat(value).toFixed(2) },
      });
      return false;
    }
    return true;
  };

  const getMinValueForField = (name: string) => {
    switch (name) {
      case "width":
        return M2M_ROMAN_BLINDS_MIN_WIDTH;
      case "height":
        return M2M_ROMAN_BLINDS_MIN_HEIGHT;
      case "installationHeight":
        return minInstallHeight;
      default:
        return "0";
    }
  };

  const clearPriceError = () => {
    if (priceError?.type)
      setPriceError({ heading: "", body: "", type: "", details: undefined });
  };

  const updateMeasurementFields = (e: ChangeEvent) => {
    if (!(e.target instanceof HTMLInputElement)) return;

    const { name, value } = e.target;

    clearPriceError();

    // Restrict field values to 2 decimal places
    if (value.includes(".")) {
      const formatDecimal = formatDecimalValue(value, name);
      if (!formatDecimal) return;
    }

    const valueAsFloat = parseFloat(value);
    const minValue = getMinValueForField(name);
    let valid = valueAsFloat > 0 && valueAsFloat >= parseInt(minValue);

    let errorType = "",
      errorValue = "";

    if (!valid) {
      errorType = "min";
      errorValue = minValue;
    }

    if (valid && name === "width" && parseFloat(maxRailSize) > 0) {
      valid = valueAsFloat <= parseFloat(maxRailSize);
      errorType = "max";
      errorValue = maxRailSize;
    }

    if (valid && name === "height" && parseFloat(maxDrop) > 0) {
      valid = valueAsFloat <= parseFloat(maxDrop);
      errorType = "max";
      errorValue = maxDrop;
    }

    const errorMsg =
      !valid && value.length > 1
        ? getErrorMessage(name, errorType, errorValue)
        : "";

    const fields = {
      [name]: { value, valid, error: errorMsg },
    };

    const currentMeasurementFields = measurementFields;

    if (name === "height") {
      setInstallHeightHint(valid ? `, for a drop of ${value}cm` : "");

      if (currentMeasurementFields.installationHeight.valid) {
        if (value > currentMeasurementFields.installationHeight.value) {
          currentMeasurementFields.installationHeight = {
            value: "",
            valid: false,
            error: "",
          };
        }
      }
    }

    setMeasurementsFields({ ...currentMeasurementFields, ...fields });
  };

  const updateRadioButton = (e: ChangeEvent) => {
    if (!(e.target instanceof HTMLInputElement)) return;

    const { name, value } = e.target;

    const fields = {
      [name]: { value },
    };

    const updatedFields = { ...radioButtonValues, ...fields };

    setRadioButtonValues(updatedFields);
  };

  const reset = () => {
    if (isReviewButtonEnabled) setIsReviewButtonEnabled(false);
    if (totalPrice) setTotalPrice("");
    if (blackoutPrice) setBlackoutPrice("");
  };

  const updatePrice = async () => {
    setIsPriceCalculating(true);
    clearPriceError();
    const configuration = getConfigFromFormData();
    const { price, error } = await getM2MPriceWithBlackout(
      runtimeConfig.aceApiBaseUrl,
      "MadeToMeasureRomanBlind",
      getFabric(),
      1,
      configuration,
    );

    setIsPriceCalculating(false);

    if (price) {
      setTotalPrice(price.total);
      if (price.blackoutLiningPrice)
        setBlackoutPrice(price.blackoutLiningPrice);
      setIsReviewButtonEnabled(true);
    }
    if (error) {
      setPriceError(error);
      if (error?.details?.maxRailSize) {
        setMaxRailSize(error.details.maxRailSize);
        setMeasurementsFields({
          ...measurementFields,
          width: { value: "", valid: false, error: "" },
        });
      }
      if (error?.details?.maxDrop) {
        setMaxDrop(error.details.maxDrop);
        setMeasurementsFields({
          ...measurementFields,
          height: { value: "", valid: false, error: "" },
        });
      }
    }
  };

  const requestPrice = () => {
    if (checkAllMeasurementsValid()) {
      updatePrice();
    } else {
      reset();
    }
  };

  useEffect(() => {
    if (checkAllMeasurementsValid()) {
      const total = parseFloat(totalPrice.replace("£", ""));
      const blackout = parseFloat(blackoutPrice.replace("£", ""));

      setTotalPrice(
        `£${(radioButtonValues.liningWeight.value === LiningWeight.Standard ? total - blackout : total + blackout).toFixed(2)}`,
      );
    }
  }, [radioButtonValues.liningWeight.value]);

  useEffect(() => {
    if (
      parseInt(measurementFields.height.value) >
      parseInt(M2M_ROMAN_BLINDS_MIN_INSTALLATION_HEIGHT)
    ) {
      setMinInstallHeight(measurementFields.height.value);
    } else if (minInstallHeight > M2M_ROMAN_BLINDS_MIN_INSTALLATION_HEIGHT) {
      setMinInstallHeight(M2M_ROMAN_BLINDS_MIN_INSTALLATION_HEIGHT);
    }
  }, [measurementFields.height.value]);

  useEffect(() => {
    const imageSuffixMap: { [key: string]: string } = {
      "Self Stack": "WSRB",
      Waterfall: "WSWF",
    };

    const previewImageUrl = getPreviewImageUrl(
      imageSuffixMap[radioButtonValues.stackingStyle.value],
    );
    if (previewImageUrl) setSelectedPreviewImage(previewImageUrl);
  }, [radioButtonValues.stackingStyle.value]);

  useEffect(() => {
    if (radioButtonValues.stackingStyle.value) requestPrice();
  }, [radioButtonValues.stackingStyle.value]);

  useEffect(() => {
    if (checkAllMeasurementsValid()) {
      const config = getConfigFromFormData();
      setRomanBlindsCookieFromConfig(config);
    }
  }, [
    radioButtonValues.liningColour.value,
    radioButtonValues.controlSide.value,
    radioButtonValues.stackingStyle.value,
    radioButtonValues.liningWeight.value,
    measurementFields.width.value,
    measurementFields.height.value,
    measurementFields.installationHeight.value,
  ]);

  useEffect(() => {
    const configuration = getConfigFromRomanBlindsCookie();
    if (configuration) setFormDataFromConfig(configuration);
  }, []);

  return (
    <div className={styles.RomanBlindsForm} data-testid="m2m-roman-blinds-form">
      <h3
        className={styles.RomanBlindsForm__title}
        data-testid="m2m-form-title"
      >
        Create your roman blind to get a price
      </h3>
      <div
        data-testid="m2m-form-description"
        className={styles.RomanBlindsForm__formDescription}
      >
        <p>
          Select a stacking style, select a window fitting, choose your control
          options, add your measurements and choose your lining options. Herbert
          Parkinson, our exclusive manufacturer in Lancashire, will create your
          blinds and send them within 7-14 working days.
        </p>
        <p>
          Blind cords and chains can present a safety risk for children and
          pets. We advise you to read and understand the information in the{" "}
          <Link
            href="/customer-services/shopping-with-us/product-recalls/product-safety-for-blinds-windows"
            target="blank"
          >
            child safety hub
          </Link>{" "}
          before placing your order.
        </p>
      </div>
      <form
        className={styles.RomanBlindsForm__form}
        aria-label="Made to Measure Roman Blinds form"
      >
        <div
          className={styles.RomanBlindsForm__accordionSection}
          data-testid="m2m-roman-blinds-accordions-container"
        >
          <Accordion>
            <AccordionItem
              id="accordion-m2m-roman-blinds-stacking-style"
              header={
                <div className={styles.RomanBlindsForm__accordionTitle}>
                  1. Select your stacking style
                </div>
              }
              isOpen={true}
              aria-label="1. Select your stacking style"
            >
              <div>
                <div className={styles.RomanBlindsForm__help_link}>
                  <UnderlinedButton
                    tag="a"
                    type="button"
                    onClick={() => toggleHelpModal(helpSections[0])}
                    labelText="Learn about stacking styles"
                  />
                </div>
                <div className={styles.OptionsListContainer}>
                  <div className={styles.OptionsList}>
                    <div className={styles.OptionsList__radio}>
                      <img src="https://static.johnlewiscontent.com/v1/static/roman-stacking-standard" />
                      <RadioButton
                        id="self-stack"
                        label={StackingStyle.SelfStack}
                        value={StackingStyle.SelfStack}
                        name="stackingStyle"
                        checked={
                          radioButtonValues.stackingStyle.value ===
                          StackingStyle.SelfStack
                        }
                        onChange={updateRadioButton}
                        isControlled
                      />
                    </div>
                    <div className={styles.OptionsList__radio}>
                      <img src="https://static.johnlewiscontent.com/v1/static/roman-stacking-waterfall" />
                      <RadioButton
                        id="waterfall"
                        label={StackingStyle.Waterfall}
                        value={StackingStyle.Waterfall}
                        name="stackingStyle"
                        checked={
                          radioButtonValues.stackingStyle.value ===
                          StackingStyle.Waterfall
                        }
                        onChange={updateRadioButton}
                        isControlled
                      />
                    </div>
                  </div>
                  {selectedPreviewImage && (
                    <div className={styles.OptionsList}>
                      <img
                        className={styles.OptionsList__preview}
                        src={selectedPreviewImage}
                        alt="Preview Stacking Style"
                      />
                    </div>
                  )}
                </div>
              </div>

              <HorizontalRule />

              <div className={styles.RomanBlindsForm__chainSection}>
                <div
                  data-testid="m2m-chain-description"
                  className={styles.RomanBlindsForm__chainSectionTitle}
                >
                  <p>Choose your chain side</p>
                  <p>Please note the colour of the chain will be chrome</p>
                </div>
                <div data-testid="m2m-chain-options">
                  <OptionList name="controlSide" isButtonList={false}>
                    <RadioButton
                      id="chain-right"
                      label="Right"
                      value="Right"
                      checked={radioButtonValues.controlSide.value === "Right"}
                      onChange={updateRadioButton}
                      isControlled
                    />
                    <RadioButton
                      id="chain-left"
                      label="Left"
                      value="Left"
                      checked={radioButtonValues.controlSide.value === "Left"}
                      onChange={updateRadioButton}
                      isControlled
                    />
                  </OptionList>
                </div>
              </div>
            </AccordionItem>

            <AccordionItem
              id="accordion-m2m-roman-blinds-measurements"
              header={
                <div className={styles.RomanBlindsForm__accordionTitle}>
                  2. Enter your measurements
                </div>
              }
              isOpen={false}
              aria-label="2. Enter your measurements"
            >
              <div>
                <div className={styles.RomanBlindsForm__help_link}>
                  Please use our quick{" "}
                  <UnderlinedButton
                    tag="a"
                    type="button"
                    onClick={() => toggleHelpModal(helpSections[1])}
                    labelText="How To Measure guide for Roman Blinds"
                  />{" "}
                  to complete this section
                </div>
                <div
                  className={styles.RomanBlindsForm__inputContainer}
                  data-testid="m2m-input-container:width"
                >
                  <label
                    data-testid="m2m-input-label:width"
                    className={styles.RomanBlindsForm__inputLabel}
                  >
                    Final width (including clearance)
                  </label>
                  <div className={styles.RomanBlindsForm__inputMeasurement}>
                    <TextInput
                      aria-label="width in centimeters"
                      id="width"
                      name="width"
                      type="number"
                      labelText="e.g. 150.5"
                      required={true}
                      readOnly={false}
                      value={measurementFields.width.value}
                      className={cx(styles.RomanBlindsForm__inputField, {
                        InputInvalid: measurementFields.width.error,
                      })}
                      min={M2M_ROMAN_BLINDS_MIN_WIDTH}
                      max={maxRailSize}
                      data-testid="m2m-input:width"
                      onChange={updateMeasurementFields}
                      onBlur={requestPrice}
                    />
                    <span
                      className={styles.RomanBlindsForm__unit}
                      aria-hidden="true"
                    >
                      cm
                    </span>
                    {measurementFields.width.valid && (
                      <TickCircleFilled
                        size="large"
                        className={styles.tickIcon}
                      />
                    )}
                    {measurementFields.width.error && (
                      <ErrorFilled size="large" className={styles.errorIcon} />
                    )}
                  </div>
                  {priceError?.type === M2MErrorType.WIDTH && (
                    <PriceError
                      errorHeading={priceError.heading}
                      errorBody={priceError.body}
                    />
                  )}
                  {measurementFields.width.error && (
                    <div data-testid="m2m-roman-blinds-input-error:width">
                      <span className={styles.RomanBlindsForm__inputFieldError}>
                        {measurementFields.width.error}
                      </span>
                    </div>
                  )}
                  <p
                    className={styles.RomanBlindsForm__instruction}
                    data-testid="m2m-roman-blinds-input-description:width"
                  >
                    N.B. When installing inside a recess, remember to take off
                    1cm from your narrowest measured width in order to ensure
                    sufficient clearance. Minimum width is 42cm
                  </p>
                </div>
              </div>

              <div
                className={styles.RomanBlindsForm__inputContainer}
                data-testid="m2m-roman-blinds-input-container:height"
              >
                <label
                  data-testid="m2m-roman-blinds-input-label:height"
                  className={styles.RomanBlindsForm__inputLabel}
                >
                  Drop
                </label>
                <div className={styles.RomanBlindsForm__inputMeasurement}>
                  <TextInput
                    aria-label="height in centimeters"
                    id="height"
                    name="height"
                    type="number"
                    labelText="e.g. 165.2"
                    required={true}
                    readOnly={false}
                    value={measurementFields.height.value}
                    className={cx(styles.RomanBlindsForm__inputField, {
                      InputInvalid: measurementFields.height.error,
                    })}
                    min={M2M_ROMAN_BLINDS_MIN_HEIGHT}
                    max={maxDrop}
                    data-testid="m2m-roman-blinds-input:height"
                    onChange={updateMeasurementFields}
                    onBlur={requestPrice}
                  />
                  <span
                    className={styles.RomanBlindsForm__unit}
                    aria-hidden="true"
                  >
                    cm
                  </span>
                  {measurementFields.height.valid && (
                    <TickCircleFilled
                      size="large"
                      className={styles.tickIcon}
                    />
                  )}
                  {measurementFields.height.error && (
                    <ErrorFilled size="large" className={styles.errorIcon} />
                  )}
                </div>
                {priceError?.type === M2MErrorType.HEIGHT && (
                  <PriceError
                    errorHeading={priceError.heading}
                    errorBody={priceError.body}
                  />
                )}
                {measurementFields.height.error && (
                  <div data-testid="m2m-roman-blinds-input-error:height">
                    <span className={styles.RomanBlindsForm__inputFieldError}>
                      {measurementFields.height.error}
                    </span>
                  </div>
                )}
                <p
                  className={styles.RomanBlindsForm__instruction}
                  data-testid="m2m-roman-blinds-input-description:height"
                >
                  Minimum drop is {M2M_ROMAN_BLINDS_MIN_HEIGHT}cm
                </p>
              </div>

              <div
                className={styles.RomanBlindsForm__inputContainer}
                data-testid="m2m-roman-blinds-input-container:installation-height"
              >
                <label
                  data-testid="m2m-roman-blinds-input-label:installation-height"
                  className={styles.RomanBlindsForm__inputLabel}
                >
                  Installation height (to floor)
                </label>
                <div className={styles.RomanBlindsForm__inputMeasurement}>
                  <TextInput
                    aria-label="installation height in centimeters"
                    id="installationHeight"
                    name="installationHeight"
                    type="number"
                    labelText="e.g. 240.2"
                    required={true}
                    readOnly={false}
                    value={measurementFields.installationHeight.value}
                    className={cx(styles.RomanBlindsForm__inputField, {
                      InputInvalid: measurementFields.installationHeight.error,
                    })}
                    min={minInstallHeight}
                    data-testid="m2m-roman-blinds-input:installation-height"
                    onChange={updateMeasurementFields}
                    onBlur={requestPrice}
                  />
                  <span
                    className={styles.RomanBlindsForm__unit}
                    aria-hidden="true"
                  >
                    cm
                  </span>
                  {measurementFields.installationHeight.valid && (
                    <TickCircleFilled
                      size="large"
                      className={styles.tickIcon}
                    />
                  )}
                  {measurementFields.installationHeight.error && (
                    <ErrorFilled size="large" className={styles.errorIcon} />
                  )}
                </div>
                {measurementFields.installationHeight.error && (
                  <div data-testid="m2m-roman-blinds-input-error:installation-height">
                    <span className={styles.RomanBlindsForm__inputFieldError}>
                      {measurementFields.installationHeight.error}
                    </span>
                  </div>
                )}
                <div
                  data-testid="m2m-roman-blinds-input-description:installation-height"
                  className={styles.RomanBlindsForm__instruction}
                >
                  <p>
                    N.B. This is measured vertically from the fixing point of
                    the blind down to the floor (even if installing above a
                    kitchen sink/worktop). It is required in order to comply
                    with strict child safety legislation.
                  </p>
                  <p>
                    The minimum installation height is {minInstallHeight}cm
                    {minInstallHeightHint}
                  </p>
                  <p>
                    To calculate the chain length expected on your blind deduct
                    150cm from your installation height.
                  </p>
                </div>
              </div>
            </AccordionItem>

            <AccordionItem
              id="accordion-m2m-roman-blinds-roll-direction"
              data-testid="m2m-roman-blinds-accordion:roll-direction"
              header={
                <div className={styles.RomanBlindsForm__accordionTitle}>
                  3. Choose lining options
                </div>
              }
              isOpen={false}
              aria-label="3. Choose lining options"
            >
              <div className={styles.RomanBlindsForm__liningDescription}>
                <p>
                  Please note: A blackout blind fitted INSIDE a recess will not
                  block out light completely. The mechanism requires a 1cm gap
                  to operate, so a &apos;frame&apos; of light will shine
                  through. Also some blackout blinds have a Stab Stitch detail
                  resulting in pinholes which cannot be prevented.
                </p>
                <p>
                  If complete blackout is required, please consider installing a
                  blind OUTSIDE the recess.
                </p>
                <div className={styles.RomanBlindsForm__help_link}>
                  <UnderlinedButton
                    tag="a"
                    type="button"
                    onClick={() => toggleHelpModal(helpSections[2])}
                    className={styles.RomanBlindsForm__help_link}
                    data-testid="m2m-roman-blinds-help:lining-types"
                    labelText="Learning about lining types"
                  />
                </div>

                <OptionList name="liningWeight" isButtonList={false}>
                  <RadioButton
                    id="premium-cotton-lining"
                    label="Premium Cotton Lining (£0)"
                    value={LiningWeight.Standard}
                    name="liningWeight"
                    checked={
                      radioButtonValues.liningWeight.value ===
                      LiningWeight.Standard
                    }
                    onChange={updateRadioButton}
                    isControlled
                  />
                  <RadioButton
                    id="thermal-blackout-lining"
                    label={`Premium Thermal Blackout Lining ${blackoutPrice ? `(+${blackoutPrice})` : "(extra cost)"}`}
                    value={LiningWeight.Blackout}
                    name="liningWeight"
                    checked={
                      radioButtonValues.liningWeight.value ===
                      LiningWeight.Blackout
                    }
                    onChange={updateRadioButton}
                    isControlled
                  />
                </OptionList>
              </div>
              <HorizontalRule />
              <OptionList name="liningColour" isButtonList={false}>
                <div className={styles.OptionsList__radio} key={"cream-lining"}>
                  <img src="https://static.johnlewiscontent.com/v1/static/lining-colour-cream" />
                  <RadioButton
                    id="cream-lining"
                    label="Cream"
                    value="Cream"
                    name="liningColour"
                    checked={radioButtonValues.liningColour.value === "Cream"}
                    onChange={updateRadioButton}
                    isControlled
                  />
                </div>
                <div className={styles.OptionsList__radio} key={"white-lining"}>
                  <img src="https://static.johnlewiscontent.com/v1/static/lining-colour-white" />
                  <RadioButton
                    id="white-lining"
                    label="White"
                    value="White"
                    name="liningColour"
                    checked={radioButtonValues.liningColour.value === "White"}
                    onChange={updateRadioButton}
                    isControlled
                  />
                </div>
              </OptionList>
            </AccordionItem>
          </Accordion>
        </div>

        <div
          className={styles.RomanBlindsForm__message}
          data-testid="m2m-roman-blinds-measurements-info-message"
        >
          <DEPRECATED_Message
            title="Please double check your measurements, as we custom make every blind."
            type="information"
          />
        </div>
        <div
          className={styles.RomanBlindsForm__summary}
          data-testid="m2m-roman-blinds-form-summary-container"
        >
          <div
            className={styles.RomanBlindsForm__summaryProduct}
            data-testid="m2m-roman-blinds-form-summary-product-container"
          >
            <p data-testid="m2m-roman-blinds-form-summary-product-title">
              {formatM2MTitle(variant.title, true)}
            </p>
            <Price
              price={totalPrice ? { now: totalPrice } : priceFrom}
              pricePrefix={
                totalPrice ? "Total Blind Price" : "Fabric Price per metre"
              }
              usageContext="pdp"
              showPriceReduction={false}
            />
          </div>
          <div
            className={styles.RomanBlindsForm__summaryCta}
            data-testid="m2m-roman-blinds-form-summary-cta-container"
          >
            <PrimaryButton
              tag="button"
              type="button"
              disabled={!isReviewButtonEnabled}
              submitting={isPriceCalculating}
              onClick={() => setIsReviewModalOpen(!isReviewModalOpen)}
              data-testid="m2m-roman-blinds-form-summary-cta"
              labelText="Review your Roman Blind"
              width="full-width"
            />
          </div>
        </div>
        {priceError?.type === M2MErrorType.GENERAL && (
          <PriceError
            errorHeading={priceError.heading}
            errorBody={priceError.body}
          />
        )}
      </form>

      <MadeToMeasureHelpModal
        visibleSection={helpModal.visibleSection}
        isOpen={helpModal.open}
        onClose={toggleHelpModal}
        sections={helpSections}
      />

      {isReviewModalOpen && (
        <MadeToMeasureReviewModal
          configuration={getConfigFromFormData()}
          totalPrice={totalPrice}
          product={product}
          runtimeConfig={runtimeConfig}
          flags={flags}
          isOpen
          onClose={() => setIsReviewModalOpen(false)}
          configurationReqType={"MadeToMeasureRomanBlind"}
        />
      )}
    </div>
  );
};
